import React, { useEffect, useMemo, useState } from "react";
import { Autocomplete, TextField, Chip, CircularProgress } from "@mui/material";

import { LOYALTY_API_BASE } from "../../constants";
import { loyaltyFetch } from "../../hooks/loyaltyFetch";
import { autocompletePopperSlotProps } from "../../utils/autocompletePopperSlotProps";

/**
 * Multi-select « coupons liés » — options via GET /admin/ui-options/coupon-types.
 */
const CouponTypesMultiSelect = ({
    brandName,
    value = [],
    onChange,
    disabled = false,
    label = "Types de coupon liés",
    helperText,
    required = false,
    optionsEndpoint = "/admin/ui-options/coupon-types",
}) => {
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!brandName) {
            setOptions([]);
            return;
        }
        let cancelled = false;
        setLoading(true);
        loyaltyFetch(`${LOYALTY_API_BASE}${optionsEndpoint}`, {
            headers: { "X-Brand": brandName },
        })
            .then(async (res) => {
                const data = res.ok ? await res.json().catch(() => null) : null;
                const list = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);
                if (!cancelled) setOptions(list);
            })
            .catch(() => { if (!cancelled) setOptions([]); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [brandName, optionsEndpoint]);

    const selected = useMemo(() => {
        const idSet = new Set((value || []).map(String));
        const fromOptions = options.filter((opt) => idSet.has(String(opt.id)));
        const known = new Set(fromOptions.map((o) => String(o.id)));
        const orphans = [...idSet]
            .filter((id) => !known.has(id))
            .map((id) => ({ id, name: `${id.slice(0, 8)}… (hors liste)` }));
        return [...fromOptions, ...orphans];
    }, [options, value]);

    const defaultHelper = loading
        ? "Chargement des types de coupon…"
        : options.length === 0
            ? "Créez d'abord un type de coupon"
            : `${options.length} type(s) disponible(s)`;

    return (
        <Autocomplete
            multiple
            disabled={disabled || !brandName}
            loading={loading}
            options={options}
            slotProps={autocompletePopperSlotProps}
            value={selected}
            isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
            getOptionLabel={(o) => o.name || String(o.id)}
            onChange={(_, vals) => onChange(vals.map((v) => String(v.id)))}
            noOptionsText={loading ? "Chargement…" : "Aucun type de coupon"}
            renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                    <Chip
                        {...getTagProps({ index })}
                        key={option.id}
                        label={option.name || option.id}
                        size="small"
                        sx={{ fontWeight: 600, fontSize: 11 }}
                    />
                ))
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    fullWidth
                    required={required}
                    size="small"
                    label={label}
                    helperText={helperText ?? defaultHelper}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress size={14} sx={{ mr: 1 }} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
};

export default CouponTypesMultiSelect;
